import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Info, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Button } from '../ui/button';

const HeroCarousel = ({ slides = [] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();

  const goToSlide = useCallback((index) => {
    if (isTransitioning || slides.length === 0) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning, slides.length]);

  const nextSlide = useCallback(() => {
    if (slides.length === 0) return;
    goToSlide((currentSlide + 1) % slides.length);
  }, [currentSlide, slides.length, goToSlide]);

  const prevSlide = useCallback(() => {
    if (slides.length === 0) return;
    goToSlide((currentSlide - 1 + slides.length) % slides.length);
  }, [currentSlide, slides.length, goToSlide]);

  useEffect(() => {
    if (slides.length === 0) return;
    const timer = setInterval(nextSlide, 8000);
    return () => clearInterval(timer);
  }, [nextSlide, slides.length]);

  const handlePlay = (slide) => {
    const path = slide.type === 'movie' ? `/movie/${slide.streamId}` : `/series/${slide.streamId}`;
    navigate(`${path}?play=true`);
  };

  const handleInfo = (slide) => {
    const path = slide.type === 'movie' ? `/movie/${slide.streamId}` : `/series/${slide.streamId}`;
    navigate(path);
  };

  if (slides.length === 0) {
    return (
      <div className="h-[60vh] min-h-[400px] bg-gradient-to-b from-purple-900/20 to-[#0a0a0b] flex items-center justify-center">
        <p className="text-gray-400">No featured content available</p>
      </div>
    );
  }

  const currentContent = slides[currentSlide];

  return (
    <div className="relative h-[85vh] min-h-[600px] overflow-hidden">
      {/* Background Images */}
      {slides.map((slide, index) => (
        <div
          key={slide.id || index}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.backdrop}
            alt={slide.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/1920x1080?text=No+Image';
            }}
          />
          {/* Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b] via-[#0a0a0b]/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-transparent" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
        <div className="max-w-2xl space-y-6">
          {/* Title */}
          <h1 
            className="text-4xl sm:text-6xl font-bold text-white opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}
          >
            {currentContent.title}
          </h1>

          {/* Meta Info */}
          <div 
            className="flex items-center gap-4 text-sm opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}
          >
            {currentContent.rating && currentContent.rating !== '0' && (
              <span className="flex items-center gap-1 text-yellow-400">
                <Star className="w-4 h-4 fill-yellow-400" />
                {currentContent.rating}/10
              </span>
            )}
            {currentContent.year && (
              <span className="text-gray-300">{currentContent.year}</span>
            )}
            <div className="flex gap-2">
              {currentContent.genres?.slice(0, 2).map((genre, i) => (
                <span
                  key={i}
                  className="px-2 py-1 bg-white/10 rounded text-xs text-gray-300"
                >
                  {genre}
                </span>
              ))}
            </div>
          </div>

          {/* Description */}
          <p 
            className="text-gray-300 text-sm sm:text-base line-clamp-3 opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
          >
            {currentContent.description}
          </p>

          {/* Buttons */}
          <div 
            className="flex gap-4 opacity-0 animate-fade-in-up"
            style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}
          >
            <Button
              onClick={() => handlePlay(currentContent)}
              className="bg-white text-black hover:bg-gray-200 px-6 py-6 text-base font-semibold"
            >
              <Play className="w-5 h-5 mr-2 fill-black" />
              Play
            </Button>
            <Button
              onClick={() => handleInfo(currentContent)}
              variant="outline"
              className="border-gray-500 text-white hover:bg-white/10 px-6 py-6 text-base"
            >
              <Info className="w-5 h-5 mr-2" />
              More Info
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Slide Indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentSlide
                  ? 'bg-white w-8'
                  : 'bg-white/50 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroCarousel;
